import React, {
  useContext, useEffect, useState, useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Button, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import './twoFactor.css';
import { UserContext } from '../../contexts/User';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';
import { NotificationContext } from '../../contexts/Notification';
import useQueryParams from '../../hooks/useQueryParams';
import FormLayout from '../../components/FormLayout';
import { TextInput } from '../../components/muiCustomized/TextInput';

function TwoFactorAuthentication() {
  const {
    twoFactorToken, qrCode, secretKey, redirect,
    signIn, updateTokens, setWorkspaceMember,
    activeWorkspace,
  } = useContext(UserContext);
  const history = useHistory();
  const queryParams = useQueryParams();
  const { displayNotification } = useContext(NotificationContext);
  const submitBtnRef = useRef(null);
  const [formData, setFormData] = useState({ code: '', qrCode: !!qrCode });
  const [formErrors, setFormErrors] = useState();
  const [hasInputError, setHasInputError] = useState(false);

  const handleRedirect = (role) => {
    // If redirect query param is passed, there is another route that requires the user to
    // sign in before proceeding. After signing in, we should redirect back to this route
    // For example, the organization signup page requires a user to sign in before creating a new organization
    // After signing in, we redirect back to the organization sign up page
    const redirect = queryParams.get('redirect');
    if (redirect) return history.push(`/${redirect}`);
    if (role === 'Superadmin') return history.push('/admin/dashboard');

    if (activeWorkspace) {
      return history.push('/dashboard');
    }

    return history.push('/workspaces');
  };

  // Redirect depending on logged in state
  useEffect(() => {
    if (!twoFactorToken) redirect('/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeTwoFactor = (e) => {
    setFormErrors({});
    setHasInputError(false);
    const { name, value } = e.target;
    // For QR code mode, only allow numeric input and limit to 6 digits
    if (qrCode && name === 'code') {
      const numericValue = value.replace(/\D/g, '').slice(0, 6);
      setFormData({ ...formData, [name]: numericValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const sendNewTwoFactorCheck = async () => {
    const response = await sendRequest({ method: 'post', url: API.TWO_FACTOR.verify, data: formData });
    if (response.success) {
      const {
        accessToken, refreshToken, userToken, role,
      } = response.data;
      if (userToken) signIn({ userToken, refreshToken });
      else {
        updateTokens({ accessToken, refreshToken });
        setWorkspaceMember(accessToken);
      }
      return handleRedirect(role);
    }

    // Enhanced error handling for QR code authentication
    if (qrCode && response.data) {
      const errorMessage = response.data.code
        || response.data.error
        || 'Invalid authenticator code. Please try again.';
      setFormErrors({ code: errorMessage });
      setHasInputError(true);

      // Clear the input field on error for better UX
      setFormData({ ...formData, code: '' });

      // Show notification for better user feedback
      displayNotification({
        message: errorMessage,
        severity: 'error',
        verticalAlign: 'top',
      });

      // Remove shake animation after a delay
      setTimeout(() => setHasInputError(false), 500);
    } else {
      setFormErrors(response.data);
    }
  };

  const sendNewTwoFactorCode = async () => {
    const response = await sendRequest({ method: 'post', url: API.TWO_FACTOR.renew, data: formData });
    if (response.success) {
      setFormErrors({});
      displayNotification({
        message: 'Successfully sent a new two factor code to your email. Please check your inbox or spam.',
        severity: 'success',
        verticalAlign: 'top',
      });
    } else {
      displayNotification({
        message: 'Unable to send a new two factor code. Please try again later.',
        severity: 'error',
        verticalAlign: 'top',
      });
    }
  };

  const copySecretKey = async () => {
    if (secretKey) {
      try {
        await navigator.clipboard.writeText(secretKey);
        displayNotification({
          message: 'Secret key copied to clipboard',
          severity: 'success',
          verticalAlign: 'top',
        });
      } catch (error) {
        // Fallback for browsers that don&apos;t support clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = secretKey;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        displayNotification({
          message: 'Secret key copied to clipboard',
          severity: 'success',
          verticalAlign: 'top',
        });
      }
    }
  };

  return (
    <FormLayout
      heading={qrCode ? 'Set up Two-Factor Authentication' : 'Check your email'}
      subheading={
        qrCode ? 'Scan the QR code below with your authenticator app' : 'We sent a code to confirm your email.'
      }
    >
      {qrCode && qrCode !== true && (
        <div className="qr-code-setup">
          <div className="qr-code-container">
            <img src={qrCode} alt="QR Code for Two-Factor Authentication" className="qr-code-image" />
          </div>
          <div className="authenticator-instructions">
            <h6>How to set up:</h6>
            <ol>
              <li>
                Download an authenticator app like Google Authenticator, Authy, or Microsoft
                Authenticator
              </li>
              <li>Open the app and tap &quot;Add account&quot; or &quot;+&quot;</li>
              <li>Choose &quot;Scan QR code&quot; and point your camera at the code above</li>
              <li>Enter the 6-digit code from your app below</li>
            </ol>
            <div className="text-muted small">
              <p>Can&apos;t scan? You can manually enter this secret key in your authenticator app:</p>
              <div className="secret-key-container">
                <code className="secret-key">{secretKey || 'Loading...'}</code>
                {secretKey && (
                  <Tooltip title="Copy secret key">
                    <IconButton
                      size="small"
                      onClick={copySecretKey}
                      className="copy-button"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              <p>
                <small>
                  Account name:
                  {' '}
                  <strong>Shoot App</strong>
                </small>
              </p>
            </div>
          </div>
        </div>
      )}
      <div className={hasInputError ? 'input-error' : ''}>
        <TextInput
          name="code"
          value={formData?.code}
          onChange={handleChangeTwoFactor}
          onKeyDown={(e) => {
            const key = e.keyCode || e.which;
            if (key === 13) {
              e.preventDefault();
              submitBtnRef.current.click();
            }
          }}
          label={qrCode ? 'Authenticator Code' : 'Two Factor Code'}
          placeholder={qrCode ? 'Enter 6-digit code from your app' : 'Enter Code'}
          data-testid="code-input"
          error={!!formErrors?.code}
          helperText={
            formErrors?.code || (qrCode ? 'Enter the 6-digit code from your authenticator app' : '')
          }
        />
      </div>
      {!qrCode && (
      <p style={{
        width: '100%',
        fontSize: '12px',
        color: 'rgba(0, 0, 0, 0.54)',
        margin: '-16px 8px 20px 30px',
        textAlign: 'left',
      }}
      >
        Enter the code sent to your email address or
        {' '}
        <button
          type="button"
          onClick={sendNewTwoFactorCode}
          className="btn-link"
          style={{
            margin: 0,
            padding: 0,
            fontSize: '12px',
          }}
        >
          request a new code.
        </button>
      </p>
      )}
      <Button
        ref={submitBtnRef}
        fullWidth
        variant="contained"
        className="nextStep-btn"
        disabled={!formData?.code || (qrCode ? formData?.code.length !== 6 : formData?.code.length < 6)}
        onClick={sendNewTwoFactorCheck}
      >
        {qrCode ? 'Verify Authenticator Code' : 'Submit'}
      </Button>
      {formData.hasError && (
        <p className="text-danger" style={{ marginTop: 16 }}>
          Something went wrong. Please try again.
        </p>
      )}
    </FormLayout>
  );
}

export default TwoFactorAuthentication;
